import {
  brandEInvoiceDecision, creatorContactOf, eInvoiceFrom, eInvoiceInputFrom, eInvoiceSummaryDeviations, eInvoiceWanted, selfBillingEInvoiceDecision,
  type EInvoiceExpected, type EInvoiceFinding, type EInvoiceSummary, type EInvoiceInput, type EInvoiceProblem, type EInvoiceSellerContactSource, type EInvoiceSourceInvoice, type BrandEInvoiceReason, type SelfBillingEInvoiceReason,
} from "@talentkontor/domain";
import type { InvoiceDetail } from "./detail-data";

/**
 * E-Rechnung (XRechnung 3.0 / EN 16931, CII) aus den beim Festschreiben eingefrorenen Angaben (0055): dieselben Werte wie
 * das archivierte PDF. Nur für Belege, die die Agentur ausstellt (Rechnung an Marke oder Creator, Storno, Gutschrift).
 * Die Umwandlung selbst liegt in der Fachlogik (eInvoiceInputFrom); der Mailversand nutzt dieselbe Funktion (0066).
 */

type SellerContact = EInvoiceSellerContactSource;

/** Kann dieser Beleg als E-Rechnung ausgegeben werden (festgeschrieben, von der Agentur ausgestellt)? */
export function eInvoiceAvailable(d: InvoiceDetail): boolean {
  const i = d.invoice as unknown as Record<string, unknown>;
  const agencyIssued = i["kind"] === "agency_to_brand" || i["kind"] === "agency_to_talent" || i["self_billed"] === true;
  return agencyIssued && ["approved", "sent", "paid", "cancelled"].includes(String(i["status"])) && !!i["issuer_snapshot"] && d.lines.length > 0;
}

const s = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
const snap = (d: InvoiceDetail, key: "issuer_snapshot" | "recipient_snapshot") => (d.invoice as unknown as Record<string, unknown>)[key] as Record<string, unknown> | null | undefined;

/**
 * Kontakt des Verkäufers wie im Versand: bei Rechnungen der Agentur Telefon und Vertretung aus org_legal, Antwortadresse aus
 * org_settings; bei der Gutschrift (K-417c) ist der Creator der Verkäufer, also sein Kontakt (Vertretung bzw. Name aus dem
 * Schnappschuss, WhatsApp-Nummer und E-Mail aus dem Profil).
 */
export function sellerContactOf(d: InvoiceDetail): SellerContact {
  if (d.invoice.self_billed) {
    const tl = d.talent as unknown as { whatsapp_phone?: string | null; email?: string | null } | null;
    return creatorContactOf(snap(d, "issuer_snapshot"), { phone: tl?.whatsapp_phone ?? null, email: tl?.email ?? null });
  }
  return { phone: s(d.legal?.["phone"]), contactName: s(d.legal?.["representative_name"]), contactEmail: s(d.org?.reply_to_email) };
}

/** Elektronische Adresse des Käufers: bei der Gutschrift die Agentur (nicht die Mailadresse des Creators) */
function buyerEmailOf(d: InvoiceDetail, given: string | null | undefined): string | null {
  if (d.invoice.self_billed) return s(snap(d, "recipient_snapshot")?.["email"]) ?? s(d.org?.reply_to_email);
  return given ?? null;
}

export function eInvoiceInputOf(d: InvoiceDetail, seller: SellerContact, buyerEmail?: string | null): EInvoiceInput {
  return eInvoiceInputFrom({ invoice: d.invoice as unknown as EInvoiceSourceInvoice, lines: d.lines, original: d.original, seller, buyerEmail: buyerEmailOf(d, buyerEmail) });
}

export function eInvoiceOf(d: InvoiceDetail, seller: SellerContact, buyerEmail?: string | null):
  { ok: true; xml: string; filename: string } | { ok: false; problems: EInvoiceProblem[] } {
  const r = eInvoiceFrom({ invoice: d.invoice as unknown as EInvoiceSourceInvoice, lines: d.lines, original: d.original, seller, buyerEmail: buyerEmailOf(d, buyerEmail) });
  return r.ok ? { ok: true, xml: r.xml, filename: r.filename } : { ok: false, problems: r.problems };
}

/**
 * K-417c (0335): Geht diese Gutschrift an den Creator zusätzlich als E-Rechnung? Dieselbe Regel wie im Versand
 * (selfBillingEInvoiceDecision): Wahl am Creator, sonst Deutschland an Deutschland, regelbesteuert, ab Stichtag.
 */
export function selfBillingEInvoiceOf(d: InvoiceDetail): { wanted: boolean; reason: SelfBillingEInvoiceReason; from: string } | null {
  const i = d.invoice as unknown as Record<string, unknown>;
  if (!d.invoice.self_billed || d.invoice.imported) return null;
  const issuer = snap(d, "issuer_snapshot");
  const recipient = snap(d, "recipient_snapshot");
  const tl = d.talent as unknown as { country?: string | null; small_business?: boolean | null; self_billing_e_invoice?: boolean | null } | null;
  const org = d.org as unknown as { country?: string | null; self_billing_e_invoice_from?: string | null } | null;
  return selfBillingEInvoiceDecision({
    creatorChoice: tl?.self_billing_e_invoice ?? null, agencyFrom: org?.self_billing_e_invoice_from ?? null,
    // eingefroren, wenn festgeschrieben; im Entwurf die Angaben von heute
    creatorCountry: s(issuer?.["country"]) ?? tl?.country ?? null, agencyCountry: s(recipient?.["country"]) ?? org?.country ?? null,
    taxMode: s(i["tax_mode"]), smallBusiness: issuer ? issuer["small_business"] === true : tl?.small_business === true,
    issueDate: i["issue_date"] ?? new Date().toISOString().slice(0, 10), serviceEnd: i["service_period_end"],
  });
}

/**
 * Steuerprüfung L11: Geht diese Rechnung der Agentur an die Marke zusätzlich als E-Rechnung? Dieselbe Regel wie im Versand
 * (brandEInvoiceDecision): Wunsch der Marke, sonst Deutschland an Deutschland, Marke Unternehmerin, kein Kleinunternehmer, über
 * 250 € brutto, ab Stichtag. Festgeschrieben aus den eingefrorenen Angaben, im Entwurf aus denen von heute.
 */
export function brandEInvoiceOf(d: InvoiceDetail): { wanted: boolean; reason: BrandEInvoiceReason; from: string } | null {
  // Aus dem bisherigen System übernommene Rechnungen (0064) haben ihr Original; eine nachgebaute E-Rechnung wäre ein zweiter Beleg
  if (d.invoice.kind !== "agency_to_brand" || d.invoice.imported) return null;
  const i = d.invoice as unknown as Record<string, unknown>;
  const issuer = snap(d, "issuer_snapshot");
  const recipient = snap(d, "recipient_snapshot");
  const org = d.org as unknown as { country?: string | null; small_business?: boolean | null; self_billing_e_invoice_from?: string | null } | null;
  const brand = d.brand as unknown as { country?: string | null; is_business?: boolean | null } | null;
  return brandEInvoiceDecision({
    brandChoice: eInvoiceWanted(d.brand, d.billingAgency), agencyFrom: org?.self_billing_e_invoice_from ?? null,
    agencyCountry: s(issuer?.["country"]) ?? org?.country ?? null, recipientCountry: s(recipient?.["country"]) ?? brand?.country ?? null,
    recipientBusiness: recipient ? recipient["business"] : brand?.is_business, taxMode: s(i["tax_mode"]),
    smallBusiness: issuer ? issuer["small_business"] === true : org?.small_business === true,
    grossCents: Number(i["gross_cents"] ?? 0), currency: s(i["currency"]),
    issueDate: i["issue_date"] ?? new Date().toISOString().slice(0, 10), serviceEnd: i["service_period_end"],
  });
}

/**
 * Geht diese Rechnung beim Versand zusätzlich als E-Rechnung raus? Rechnungen der Agentur an eine Marke (auch Stornorechnungen)
 * nach brandEInvoiceOf (Wunsch der Marke bzw. ihrer abrechnenden Agentur, 0066, oder Vorgabe ab Stichtag, L11). Creator-Rechnungen
 * an die Marke stellt der Creator aus; sie gehen weiter nur als PDF.
 */
export function eInvoiceGoesWithMail(d: InvoiceDetail): boolean {
  if (d.invoice.self_billed) return selfBillingEInvoiceOf(d)?.wanted === true;
  return brandEInvoiceOf(d)?.wanted === true;
}

/** Die Marke wünscht E-Rechnungen, die Rechnung stellt aber der Creator aus (Hinweis im Versand) */
export function eInvoiceNotForCreatorInvoice(d: InvoiceDetail): boolean {
  return d.invoice.kind === "talent_to_brand" && eInvoiceWanted(d.brand, d.billingAgency);
}

// ── Steuerprüfung L12: empfangene E-Rechnungen an Creator-Rechnungen ───────────────────────────────────────────────

/** Rechnung des Creators (eigene Datei, keine Gutschrift der Agentur): dort ist eine E-Rechnung die Rechnung selbst */
export function isCreatorIssued(d: Pick<InvoiceDetail, "invoice">): boolean {
  return (d.invoice.kind === "talent_to_agency" || d.invoice.kind === "talent_to_brand") && !d.invoice.self_billed;
}

/** Die E-Rechnung, die als Rechnung gilt: die Rechnungsdatei selbst, sonst die zuletzt abgelegte E-Rechnung der Art „Rechnung“ */
export function leadingEInvoice(d: Pick<InvoiceDetail, "invoice" | "attachments">): (InvoiceDetail["attachments"][number] & { e_invoice: EInvoiceSummary }) | null {
  const list = d.attachments.filter((a): a is InvoiceDetail["attachments"][number] & { e_invoice: EInvoiceSummary } => !!a.e_invoice);
  const pdf = d.invoice.pdf_path as string | null;
  return list.find((a) => a.storage_path === pdf) ?? list.filter((a) => a.category === "invoice").pop() ?? null;
}

/**
 * Werte im Portal, mit denen die E-Rechnung verglichen wird: Angaben der Rechnung, Steuernummer bzw. USt-IdNr. aus dem
 * Creator-Profil, Empfänger (Agentur bzw. Marke) und der angeforderte Monat (Rechnungsanforderung der Agentur).
 */
export function eInvoiceExpectedOf(d: InvoiceDetail, orgName: string): EInvoiceExpected {
  const i = d.invoice;
  const tl = d.talent as unknown as { vat_id?: string | null; tax_number?: string | null } | null;
  const recipient = snap(d, "recipient_snapshot");
  const buyer = s(recipient?.["name"]) ?? (i.kind === "talent_to_brand" ? d.brand?.name ?? null : d.org?.legal_name ?? orgName);
  return {
    number: i.number, issueDate: i.issue_date, serviceFrom: s(i["service_period_start" as keyof typeof i]), serviceTo: s(i["service_period_end" as keyof typeof i]), currency: i.currency,
    netCents: i.net_cents, taxCents: i.tax_cents, grossCents: i.gross_cents, sellerVatId: tl?.vat_id ?? null, sellerTaxNumber: tl?.tax_number ?? null,
    buyerName: buyer, month: d.period?.month ?? null,
  };
}

/** Abweichungen der E-Rechnung einer Creator-Rechnung (Hinweis in der Rechnungsansicht) */
export function eInvoiceDeviationsOf(d: InvoiceDetail, orgName: string, lang: "de" | "en" = "de"): EInvoiceFinding[] {
  const lead = isCreatorIssued(d) ? leadingEInvoice(d) : null;
  return lead && lead.e_invoice.origin === "uploaded" ? eInvoiceSummaryDeviations(lead.e_invoice, eInvoiceExpectedOf(d, orgName), lang) : [];
}

/** Wohin der Dialog „Für die E-Rechnung fehlt noch etwas“ führt (K-433) */
export interface ERechnungZiel { kind: "creator" | "firma" | "marke"; to: string }

/**
 * K-433: Knöpfe im Dialog je nach fehlender Angabe. Gutschrift an den Creator: Angaben des Verkäufers (Creator: Telefon, E-Mail,
 * Anschrift, Steuernummer, IBAN) im Creator-Profil des betreffenden Creators, Angaben des Käufers (Agentur) unter Einstellungen ·
 * Firma; fehlt bei beiden etwas, beide Knöpfe. Rechnung der Agentur: wie bisher Einstellungen · Firma, beim Versand dazu die Marke.
 */
export function eInvoiceFixTargets(
  problems: readonly Pick<EInvoiceProblem, "party">[], selfBilled: boolean, ids: { talentId?: string | null; brandId?: string | null }, sending = false,
): ERechnungZiel[] {
  const firma: ERechnungZiel = { kind: "firma", to: "/agentur/einstellungen/firma" };
  if (!selfBilled) return sending && ids.brandId ? [firma, { kind: "marke", to: `/agentur/marken/${ids.brandId}` }] : [firma];
  const creator: ERechnungZiel[] = problems.some((p) => p.party === "seller") && ids.talentId ? [{ kind: "creator", to: `/agentur/creator/${ids.talentId}` }] : [];
  // Agentur-Angaben, oder nichts Zuordenbares (dann wie bisher die Firmenangaben)
  return problems.some((p) => p.party === "buyer") || creator.length === 0 ? [...creator, firma] : creator;
}
